'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';

import { ChevronRight } from 'lucide-react';
import { useTheme } from 'next-themes';

import { If } from '@kit/ui/if';
import { cn } from '@kit/ui/utils';

import LogoBlack from '~/components/branding/logo-black';
import LogoGradient from '~/components/branding/logo-gradient';

import { Line } from '../line';
import BlurFade from '../magicui/blur-fade';
import { siteConfig } from '../marketing-config';
import AssociationCertificate from './association-certificate';

export default function Footer() {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  const LogoComponent = resolvedTheme === 'dark' ? LogoGradient : LogoBlack;

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <BlurFade inView className="relative z-10">
      <footer className="pt-1 text-gray-400">
        <div className="mx-auto max-w-1440 px-6 pt-16 lg:px-20 lg:py-15">
          <Line />

          <div className="flex flex-col pb-1 pt-10 lg:py-[70px] xl:flex-row xl:justify-between">
            <div className="mb-10 xl:mb-0 xl:max-w-sm">
              <div className="mb-4 font-semibold">
                {mounted ? (
                  <Link
                    href="/"
                    title="brand-logo"
                    className="relative mr-6 flex items-center space-x-2"
                  >
                    <LogoComponent className="h-[22px] w-auto" />
                  </Link>
                ) : (
                  <div className="h-[22px]]" />
                )}
              </div>
              <h2 className="mb-10 text-sm leading-6 text-white/60 xl:mb-12 xl:text-base xl:leading-normal">
                Saights - Where Data meets Decision.
              </h2>
              <AssociationCertificate />
            </div>

            <div className="flex w-full flex-row flex-wrap justify-between xl:flex-nowrap xl:justify-end xl:space-x-20">
              {siteConfig.footer.info.map((section, index) => (
                <div
                  key={index}
                  className={cn(
                    `mb-10 flex w-1/2 md:w-1/3 md:justify-start xl:mb-0 xl:block xl:w-28`,
                    index % 2 === 0 ? 'justify-start' : 'justify-end',
                  )}
                >
                  <div>
                    <h2 className="mb-6 font-semibold text-white">
                      {section.title}
                    </h2>
                    <ul className="flex flex-col gap-4 tracking-[-1.4px]">
                      {section.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          <Link
                            href={link.href}
                            className="group inline-flex cursor-pointer items-center justify-start gap-1 text-white/60 duration-200 hover:text-foreground hover:opacity-90"
                          >
                            {link.text}
                            <ChevronRight className="h-4 w-4 translate-x-0 transform opacity-0 transition-all duration-300 ease-out group-hover:translate-x-1 group-hover:opacity-100" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
              <If condition={siteConfig.footer.social.links.length > 0}>
                <div className="mb-10 w-1/2 md:w-1/3 xl:mb-0 xl:w-auto xl:pr-6">
                  <h3 className="mb-5 font-medium text-white">
                    Stay Connected
                  </h3>
                  <div className="flex space-x-4">
                    {siteConfig.footer.social.links.map((link, linkIndex) => (
                      <Link
                        href={link.href}
                        key={linkIndex}
                        className="group inline-flex cursor-pointer items-center justify-start text-white/60 duration-200 hover:text-foreground hover:opacity-90"
                      >
                        {link.icon}
                      </Link>
                    ))}
                  </div>
                </div>
              </If>
            </div>
          </div>

          <Line />

          <div className="flex flex-col items-center justify-between pb-6 pt-10 xl:flex-row xl:pb-[60px] xl:pt-[70px]">
            <span className="tracking-[-0.24px] text-white/60">
              &copy;Saypien {new Date().getFullYear()}. All Rights Reserved.
            </span>
            <ul className="mt-8 flex justify-start gap-8 text-sm tracking-tight text-white/60 md:justify-end xl:mt-0">
              <li>
                <Link href="/imprint" target="_blank" rel="noopener noreferrer">
                  Imprint
                </Link>
              </li>
              <li>
                <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/terms-of-service" rel="noopener noreferrer">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </footer>
    </BlurFade>
  );
}
